<template>
  <div class="project-detail" v-if="project">
    <header class="project-detail__header" :style="bannerStyle">
      <div class="project-detail__banner-overlay"></div>
      <div class="container project-detail__header-content">
        <button class="project-detail__back" @click="goBack">
          ← Back to projects
        </button>
        <div class="project-detail__title-section">
          <img
            v-if="project.logo"
            :src="project.logo"
            :alt="project.title + ' logo'"
            class="project-detail__logo"
          />
          <div>
            <h1 class="project-detail__title">
              {{ project.title }}
              <span class="project-detail__category">{{
                project.category
              }}</span>
            </h1>
            <p class="project-detail__description">{{ project.description }}</p>
          </div>
        </div>

        <div
          class="project-detail__meta"
          v-if="project.links || project.tech || project.team"
        >
          <img
            v-if="project.team"
            src="/team_icon.png"
            alt="Team project"
            title="This project was made in a team"
            class="project-detail__team-icon"
          />
          <div class="project-detail__tech" v-if="project.tech?.length">
            <span
              v-for="tech in project.tech"
              :key="tech"
              class="project-detail__tech-tag"
            >
              {{ tech }}
            </span>
          </div>
          <div class="project-detail__links" v-if="project.links">
            <a
              v-if="project.links.github"
              :href="project.links.github"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn--outline"
            >
              GitHub
            </a>
            <a
              v-if="project.links.live"
              :href="project.links.live"
              target="_blank"
              rel="noopener noreferrer"
              class="btn"
            >
              Open
            </a>
          </div>
        </div>
      </div>
    </header>

    <main class="container">
      <section class="project-detail__content" v-if="project.content">
        <p>{{ project.content }}</p>
      </section>

      <section class="project-detail__gallery" v-if="galleryImages.length">
        <ImageGallery :images="galleryImages" />
      </section>
    </main>
  </div>

  <div class="project-not-found" v-else>
    <div class="container">
      <h1>Project not found</h1>
      <p>The project you're looking for doesn't exist.</p>
      <router-link to="/" class="btn">Back to Home</router-link>
    </div>
  </div>
</template>

<script>
import ImageGallery from "@/components/common/ImageGallery.vue";
import { getProject } from "@/utils/projectsLoader";

export default {
  name: "ProjectDetail",
  components: {
    ImageGallery,
  },
  props: {
    category: {
      type: String,
      required: true,
    },
    slug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      project: null,
    };
  },
  computed: {
    galleryImages() {
      if (!this.project?.images) return [];
      return this.project.images.map((img, index) => ({
        src: img,
        alt: `${this.project.title} screenshot ${index + 1}`,
      }));
    },
    bannerStyle() {
      if (!this.project?.thumbnail) return {};
      return {
        backgroundImage: `url('${this.project.thumbnail}')`,
      };
    },
  },
  created() {
    this.loadProject();
  },
  watch: {
    // Reload if route params change
    $route() {
      this.loadProject();
    },
  },
  methods: {
    loadProject() {
      this.project = getProject(this.category, this.slug);
    },
    goBack() {
      this.$router.push("/");
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$primary-dark: #1d4ed8;
$text-muted: #64748b;
$border: #e2e8f0;
$bg-alt: #f8fafc;

.project-detail {
  min-height: 100vh;

  &__header {
    background-size: cover;
    background-position: center;
    padding: 3rem 0 4rem;
    position: relative;
  }

  &__banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(248, 250, 252, 0.55);
    backdrop-filter: blur(50px);
    z-index: 0;
  }

  &__header-content {
    position: relative;
    z-index: 1;
  }

  &__title-section {
    display: flex;
    gap: 2rem;
    align-items: flex-start;
    margin-bottom: 2rem;
  }

  &__logo {
    width: 100px;
    height: 100px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__back {
    background: none;
    border: none;
    color: $primary;
    font-size: 1rem;
    cursor: pointer;
    padding: 0;
    margin-bottom: 2rem;
    display: inline-block;

    &:hover {
      color: $primary-dark;
    }
  }

  &__category {
    display: inline-block;
    background-color: $primary;
    color: white;
    padding: 0.25rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    margin-left: 1rem;
    vertical-align: middle;
  }

  &__title {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  &__description {
    font-size: 1.2rem;
    color: $text-muted;
    max-width: 700px;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1.5rem;
    margin-top: 1.5rem;
  }

  &__tech {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__team-icon {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }

  &__tech-tag {
    background-color: $border;
    padding: 0.25rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 500;
  }

  &__links {
    display: flex;
    gap: 0.75rem;
  }

  &__content {
    padding-top: 3rem;
    padding-bottom: 1rem;
    font-size: 1.1rem;
    line-height: 1.8;
    max-width: 800px;

    p {
      white-space: pre-wrap;
    }
  }

  &__gallery {
    padding: 2rem 0 4rem;

    h2 {
      margin-bottom: 1.5rem;
    }
  }
}

.project-not-found {
  min-height: 100vh;
  display: flex;
  align-items: center;
  text-align: center;

  h1 {
    margin-bottom: 1rem;
  }

  p {
    color: $text-muted;
    margin-bottom: 2rem;
  }
}

@media (max-width: 768px) {
  .project-detail {
    &__title {
      font-size: 2rem;
    }

    &__meta {
      flex-direction: column;
      align-items: flex-start;
    }

    &__title-section {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    &__logo {
      width: 80px;
      height: 80px;
    }
  }
}
</style>
